import React, { useState, useCallback, useEffect } from 'react';
import { FileVideo, Video, File } from 'lucide-react';
import FileUpload from '../../components/FileUpload';
import { useTranslation } from 'react-i18next';
import { FaLink } from 'react-icons/fa';
import { updateVideoSocialLinks, deleteVideoFile, deleteFootageFile } from '../../services/trackService';
import type { Track, VideoAsset, VideoFootage } from '../../services/trackTypes';
import { useAuth } from '../../contexts/AuthContext';
import { useWebSocketNotifications } from '../../hooks/useWebSocketNotifications';
import { logInfo, logError } from '../../services/logtail';
import { asyncUploadService } from '../../services/asyncUploadService';
import FileTile from './FileTile';
import toast from 'react-hot-toast';
import CassetteLoader from '../../components/CassetteLoader';
import type { TrackPermissions } from '../../types/track';
import UploadProgressDisplay from '../../components/UploadProgressDisplay';
import BinaryUnavailablePlaceholder from '../../components/BinaryUnavailablePlaceholder';
import { resolveTrackMediaUrl, isCassetteCloudStorage } from '../../services/mediaAccess';

interface VerticalVideosSectionProps {
  track: Track;
  permissions?: TrackPermissions;
  section?: 'videos' | 'footages' | 'both';
  binaryAccess?: boolean;
  binaryInactiveReason?: BinaryInactiveReason;
  requiredStorageProvider?: string;
  viewerRole?: 'owner' | 'collaborator';
}

const getVideoIcon = (name: string, isFinal?: boolean) => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (isFinal) {
    return <Video className="w-6 h-6 text-blue-500" />;
  }
  if (ext === 'mp4') {
    return <FileVideo className="w-6 h-6 text-red-400" />;
  }
  if (ext === 'mov') {
    return <FileVideo className="w-6 h-6 text-green-400" />;
  }
  return <File className="w-6 h-6 text-gray-300" />;
};

type SocialLinkKey = 'instagramReels' | 'tiktok' | 'youtubeShorts' | 'vkClips';

const VerticalVideosSection: React.FC<VerticalVideosSectionProps> = ({
  track,
  permissions,
  section = 'both',
  binaryAccess,
  binaryInactiveReason,
  requiredStorageProvider,
  viewerRole = 'collaborator',
}) => {
  const { t } = useTranslation();
  const { user, unlinkGoogleDrive } = useAuth();
  const [footages, setFootages] = useState<VideoFootage[]>(track.videoFootages || []);
  const [videos, setVideos] = useState<VideoAsset[]>(track.videoAssets || []);
  const [pendingVideoFiles, setPendingVideoFiles] = useState<File[]>([]);
  const [pendingFootageFiles, setPendingFootageFiles] = useState<File[]>([]);
  const [isUploadingVideos, setIsUploadingVideos] = useState(false);
  const [isUploadingFootages, setIsUploadingFootages] = useState(false);
  const [uploadingVideos, setUploadingVideos] = useState<Set<string>>(new Set());
  const [uploadingFootages, setUploadingFootages] = useState<Set<string>>(new Set());
  const [videoUploadProgress, setVideoUploadProgress] = useState<Record<string, { filename: string; progress: number }>>({});
  const [videoUploadFilenames, setVideoUploadFilenames] = useState<Record<string, string>>({});
  const [videoUploadFileStatuses, setVideoUploadFileStatuses] = useState<Record<string, 'uploading' | 'completed' | 'failed'>>({});
  const [footageUploadProgress, setFootageUploadProgress] = useState<Record<string, { filename: string; progress: number }>>({});
  const [footageUploadFilenames, setFootageUploadFilenames] = useState<Record<string, string>>({});
  const [footageUploadFileStatuses, setFootageUploadFileStatuses] = useState<Record<string, 'uploading' | 'completed' | 'failed'>>({});
  const videoFolderUrl = track.videoFolderUrl;
  const footagesFolderUrl = track.footagesFolderUrl;

  // Ensure videos and footages have proper URLs (GDrive links or presigned Cassette Cloud)
  useEffect(() => {
    if (!isCassetteCloudStorage(track.storageProvider)) {
      if (videoFolderUrl) {
        setVideos((prev: VideoAsset[]) => prev.map((video: VideoAsset) => ({
          ...video,
          url: video.url || (video.fileId ? `https://drive.google.com/file/d/${video.fileId}/view` : '#'),
          socialLinks: video.socialLinks || {}
        })));
      }
      return;
    }
    let cancelled = false;
    (async () => {
      const resolvedVideos = await Promise.all((track.videoAssets || []).map(async (video) => {
        if (!video.fileId) return { ...video, url: '#', socialLinks: video.socialLinks || {} };
        try {
          const access = await resolveTrackMediaUrl(track.id, video.fileId, 'video', track.storageProvider);
          return { ...video, url: access.url, socialLinks: video.socialLinks || {} };
        } catch {
          return { ...video, url: '#', socialLinks: video.socialLinks || {} };
        }
      }));
      const resolvedFootages = await Promise.all((track.videoFootages || []).map(async (footage) => {
        if (!footage.fileId) return { ...footage, url: '#' };
        try {
          const access = await resolveTrackMediaUrl(track.id, footage.fileId, 'footage', track.storageProvider);
          return { ...footage, url: access.url };
        } catch {
          return { ...footage, url: '#' };
        }
      }));
      if (!cancelled) {
        if (resolvedVideos.length > 0) setVideos(resolvedVideos);
        if (resolvedFootages.length > 0) setFootages(resolvedFootages);
      }
    })();
    return () => { cancelled = true; };
  }, [track.id, track.storageProvider, track.videoAssets, track.videoFootages, videoFolderUrl]);

  useEffect(() => {
    if (isCassetteCloudStorage(track.storageProvider)) {
      return;
    }
    if (footagesFolderUrl) {
      setFootages((prev: VideoFootage[]) => prev.map((footage: VideoFootage) => ({
        ...footage,
        url: footage.url || (footage.fileId ? `https://drive.google.com/file/d/${footage.fileId}/view` : '#')
      })));
    }
  }, [footagesFolderUrl]);

  // Use centralized WebSocket notifications hook
  // Use WebSocket notifications for both video and footages uploads
  // Note: No fileType filter because this section handles both 'video' and 'footages'
  useWebSocketNotifications({
    trackId: track.id,
    fileTypes: ['video', 'footages'],
    onUploadComplete: (notification) => {
      const { upload_id, file_id, filename, file_type } = notification;
      
      if (file_type === 'video') {
        // Try to remove from uploading set (may not match overall upload ID, that's OK)
        setUploadingVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(upload_id);
          return newSet;
        });
        
        // Add the new video to the list (this is what makes videos appear)
        const newVideo: VideoAsset = {
          name: filename || 'Unknown video',
          fileId: file_id || '',
          url: file_id ? `https://drive.google.com/file/d/${file_id}/view` : '#'
        };
        
        setVideos((prev: VideoAsset[]) => {
          // Check if video already exists (avoid duplicates)
          const exists = prev.some(v => v.fileId === file_id);
          if (exists) return prev;
          return [...prev, newVideo];
        });
        
      } else if (file_type === 'footages') {
        // Try to remove from uploading set (may not match overall upload ID, that's OK)
        setUploadingFootages(prev => {
          const newSet = new Set(prev);
          newSet.delete(upload_id);
          return newSet;
        });
        
        // Add the new footage to the list (this is what makes footages appear)
        const newFootage: VideoFootage = {
          name: filename || 'Unknown footage',
          fileId: file_id || '',
          url: file_id ? `https://drive.google.com/file/d/${file_id}/view` : '#'
        };
        
        setFootages((prev: VideoFootage[]) => {
          // Check if footage already exists (avoid duplicates)
          const exists = prev.some(f => f.fileId === file_id);
          if (exists) return prev;
          return [...prev, newFootage];
        });
      }
    },
    onUploadFailed: (notification) => {
      const { upload_id, file_type } = notification;
      
      // Try to remove from uploading set (may not match overall upload ID, that's OK)
      // The progress display cleanup will handle the rest
      if (file_type === 'video') {
        setUploadingVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(upload_id);
          return newSet;
        });
      } else if (file_type === 'footages') {
        setUploadingFootages(prev => {
          const newSet = new Set(prev);
          newSet.delete(upload_id);
          return newSet;
        });
      }
    },
    onAuthIssue: (notification) => {
      // Handle Google Drive auth issues - the hook will automatically trigger re-auth
      logInfo('🔐 AUTH ISSUE: VerticalVideosSection received auth issue notification', { 
        notification,
        trackId: track.id,
        component: 'VerticalVideosSection'
      });
    },
    unlinkGoogleDrive: unlinkGoogleDrive
  });



  // Handle video file upload
  const handleVideoFileUpload = useCallback((files: FileList | File[]) => {
    const arr = Array.isArray(files) ? files : Array.from(files);
    setPendingVideoFiles(prev => [...prev, ...arr]);
  }, []);

  // Handle footage file upload
  const handleFootageFileUpload = useCallback((files: FileList | File[]) => {
    const arr = Array.isArray(files) ? files : Array.from(files);
    setPendingFootageFiles(prev => [...prev, ...arr]);
  }, []);

  // Handle bulk video upload
  const handleBulkVideoUpload = useCallback(async () => {
    if (pendingVideoFiles.length === 0) return;
    
    setIsUploadingVideos(true);
    
    // Declare progress tracking variables outside try block for cleanup access
    let simulatedProgressInterval: NodeJS.Timeout | null = null;
    
    try {
      if (!track.id) return;
      
      // Import chunk calculation helper
      const { calculateChunkCount } = await import('../../services/chunkUploadService');
      
      // Calculate total chunks across all files upfront
      const fileChunkCounts = pendingVideoFiles.map(file => ({
        file,
        chunkCount: calculateChunkCount(file)
      }));
      const totalChunks = fileChunkCounts.reduce((sum, item) => sum + item.chunkCount, 0);
      
      // Create a single upload ID for overall progress tracking
      const overallUploadId = `video-upload-${Date.now()}`;
      const allFileNames = pendingVideoFiles.map(f => f.name).join(', ');
      
      // Initialize overall progress immediately
      setUploadingVideos(new Set([overallUploadId]));
      setVideoUploadFilenames({ [overallUploadId]: allFileNames });
      setVideoUploadProgress({ 
        [overallUploadId]: { filename: allFileNames, progress: 0 } 
      });
      
      // Initialize file statuses - all start as 'uploading'
      const initialStatuses: Record<string, 'uploading' | 'completed' | 'failed'> = {};
      pendingVideoFiles.forEach(file => {
        initialStatuses[file.name] = 'uploading';
      });
      setVideoUploadFileStatuses(initialStatuses);
      
      // Track successful and failed uploads
      const successfulUploads: string[] = [];
      const failedUploads: string[] = [];
      
      // Calculate progress per chunk (as percentage) - constant across all files
      const progressPerChunk = 100 / totalChunks;
      
      // Simulated progress tracking - MUST be outside file loop to persist across files
      let currentSimulatedProgress = 0;
      let targetProgress = 0;
      
      // Helper to update progress
      const updateProgress = () => {
        const displayProgress = Math.min(currentSimulatedProgress, targetProgress);
        const clamped = Math.min(displayProgress, 99);
        
        setVideoUploadProgress(prev => ({
          ...prev,
          [overallUploadId]: {
            filename: allFileNames,
            progress: clamped
          }
        }));
      };
      
      // Start simulated progress timer (1% per second = 0.2% per 200ms)
      const startSimulatedProgress = () => {
        if (simulatedProgressInterval) {
          clearInterval(simulatedProgressInterval);
        }
        
        simulatedProgressInterval = setInterval(() => {
          if (currentSimulatedProgress < targetProgress) {
            currentSimulatedProgress += 0.2;
            updateProgress();
          } else {
            if (simulatedProgressInterval) {
              clearInterval(simulatedProgressInterval);
              simulatedProgressInterval = null;
            }
          }
        }, 200);
      };
      
      // Cleanup function for simulated progress
      const cleanupSimulatedProgress = () => {
        if (simulatedProgressInterval) {
          clearInterval(simulatedProgressInterval);
          simulatedProgressInterval = null;
        }
      };
      
      // Initialize progress - start from 0%
      updateProgress();
      startSimulatedProgress();
      
      // Upload files sequentially (one at a time)
      for (let fileIndex = 0; fileIndex < pendingVideoFiles.length; fileIndex++) {
        const file = pendingVideoFiles[fileIndex];
        const fileChunkCount = fileChunkCounts[fileIndex].chunkCount;
        
        try {
          // Calculate chunks from previous files
          const previousFileChunks = fileChunkCounts
            .slice(0, fileIndex)
            .reduce((sum, item) => sum + item.chunkCount, 0);
          
          // Set initial target to first chunk completion of this file
          targetProgress = (previousFileChunks + 1) * progressPerChunk;
          
          // Upload file with progress tracking
          const task = await asyncUploadService.uploadFile(
            track.id, 
            file, 
            'video',
            fileChunkCount === 1 ? (fileProgress) => {
              if (fileProgress >= 100) {
                targetProgress = (previousFileChunks + 1) * progressPerChunk;
                currentSimulatedProgress = targetProgress;
                updateProgress();
                cleanupSimulatedProgress();
              }
            } : undefined,
            fileChunkCount > 1 ? (chunkIndex, totalChunksForFile) => {
              if (chunkIndex === 0) return;
              
              const globalChunkIndex = previousFileChunks + chunkIndex;
              const chunkCompletionProgress = globalChunkIndex * progressPerChunk;
              
              currentSimulatedProgress = chunkCompletionProgress;
              targetProgress = chunkCompletionProgress;
              updateProgress();
              
              if (globalChunkIndex < totalChunks) {
                targetProgress = (globalChunkIndex + 1) * progressPerChunk;
                startSimulatedProgress();
              } else {
                cleanupSimulatedProgress();
                currentSimulatedProgress = 100;
                targetProgress = 100;
                updateProgress();
              }
            } : undefined
          );
          
          // File upload completed
          const fileCompletionProgress = (previousFileChunks + fileChunkCount) * progressPerChunk;
          currentSimulatedProgress = fileCompletionProgress;
          targetProgress = fileCompletionProgress;
          updateProgress();
          
          // If this is not the last file, continue simulation
          const isLastFile = fileIndex === pendingVideoFiles.length - 1;
          if (!isLastFile) {
            const nextFileFirstChunk = previousFileChunks + fileChunkCount + 1;
            if (nextFileFirstChunk <= totalChunks) {
              targetProgress = nextFileFirstChunk * progressPerChunk;
              startSimulatedProgress();
            }
          } else {
            cleanupSimulatedProgress();
          }
          
          // Mark file as completed
          setVideoUploadFileStatuses(prev => ({
            ...prev,
            [file.name]: 'completed'
          }));
          
          successfulUploads.push(file.name);
        } catch (uploadError) {
          cleanupSimulatedProgress();
          
          const errorMessage = uploadError instanceof Error ? uploadError.message : String(uploadError);
          console.error('❌ Video upload failed:', {
            fileName: file.name,
            trackId: track.id,
            error: errorMessage,
            fullError: uploadError
          });
          
          logError('Failed to upload video file', { 
            trackId: track.id, 
            fileName: file.name, 
            error: errorMessage
          });
          
          // Mark file as failed
          setVideoUploadFileStatuses(prev => ({
            ...prev,
            [file.name]: 'failed'
          }));
          
          failedUploads.push(file.name);
        }
      }
      
      // All files completed - clean up and set to 100%
      cleanupSimulatedProgress();
      currentSimulatedProgress = 100;
      targetProgress = 100;
      setVideoUploadProgress(prev => ({
        ...prev,
        [overallUploadId]: {
          filename: allFileNames,
          progress: 100
        }
      }));
      
      // Clean up overall upload ID after a short delay
      setTimeout(() => {
        setUploadingVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(overallUploadId);
          return newSet;
        });
        setVideoUploadFilenames(prev => {
          const newState = { ...prev };
          delete newState[overallUploadId];
          return newState;
        });
        setVideoUploadProgress(prev => {
          const newState = { ...prev };
          delete newState[overallUploadId];
          return newState;
        });
        setVideoUploadFileStatuses({});
      }, 500);
      
      // Clear pending files
      setPendingVideoFiles([]);
      
      if (failedUploads.length > 0) {
        toast.error(`${failedUploads.length} video file(s) failed to upload: ${failedUploads.join(', ')}`);
      }
      
    } catch (error) {
      if (simulatedProgressInterval) {
        clearInterval(simulatedProgressInterval);
        simulatedProgressInterval = null;
      }
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Critical error in video upload handler:', {
        error: errorMessage,
        fullError: error,
        trackId: track.id
      });
      
      logError('Failed to start video uploads', { trackId: track.id, error: errorMessage });
      toast.error('Failed to start video uploads');
    } finally {
      setIsUploadingVideos(false);
    }
  }, [track.id, pendingVideoFiles, user?.uid]);

  // Handle bulk footage upload
  const handleBulkFootageUpload = useCallback(async () => {
    if (pendingFootageFiles.length === 0) return;
    
    setIsUploadingFootages(true);
    
    // Declare progress tracking variables outside try block for cleanup access
    let simulatedProgressInterval: NodeJS.Timeout | null = null;
    
    try {
      if (!track.id) return;
      
      // Import chunk calculation helper
      const { calculateChunkCount } = await import('../../services/chunkUploadService');
      
      // Calculate total chunks across all files upfront
      const fileChunkCounts = pendingFootageFiles.map(file => ({
        file,
        chunkCount: calculateChunkCount(file)
      }));
      const totalChunks = fileChunkCounts.reduce((sum, item) => sum + item.chunkCount, 0);
      
      // Create a single upload ID for overall progress tracking
      const overallUploadId = `footage-upload-${Date.now()}`;
      const allFileNames = pendingFootageFiles.map(f => f.name).join(', ');
      
      // Initialize overall progress immediately
      setUploadingFootages(new Set([overallUploadId]));
      setFootageUploadFilenames({ [overallUploadId]: allFileNames });
      setFootageUploadProgress({ 
        [overallUploadId]: { filename: allFileNames, progress: 0 } 
      });
      
      // Initialize file statuses - all start as 'uploading'
      const initialStatuses: Record<string, 'uploading' | 'completed' | 'failed'> = {};
      pendingFootageFiles.forEach(file => {
        initialStatuses[file.name] = 'uploading';
      });
      setFootageUploadFileStatuses(initialStatuses);
      
      // Track successful and failed uploads
      const successfulUploads: string[] = [];
      const failedUploads: string[] = [];
      
      // Calculate progress per chunk (as percentage) - constant across all files
      const progressPerChunk = 100 / totalChunks;
      
      // Simulated progress tracking - MUST be outside file loop to persist across files
      let currentSimulatedProgress = 0;
      let targetProgress = 0;
      
      // Helper to update progress
      const updateProgress = () => {
        const displayProgress = Math.min(currentSimulatedProgress, targetProgress);
        const clamped = Math.min(displayProgress, 99);
        
        setFootageUploadProgress(prev => ({
          ...prev,
          [overallUploadId]: {
            filename: allFileNames,
            progress: clamped
          }
        }));
      };
      
      // Start simulated progress timer (1% per second = 0.2% per 200ms)
      const startSimulatedProgress = () => {
        if (simulatedProgressInterval) {
          clearInterval(simulatedProgressInterval);
        }
        
        simulatedProgressInterval = setInterval(() => {
          if (currentSimulatedProgress < targetProgress) {
            currentSimulatedProgress += 0.2;
            updateProgress();
          } else {
            if (simulatedProgressInterval) {
              clearInterval(simulatedProgressInterval);
              simulatedProgressInterval = null;
            }
          }
        }, 200);
      };
      
      // Cleanup function for simulated progress
      const cleanupSimulatedProgress = () => {
        if (simulatedProgressInterval) {
          clearInterval(simulatedProgressInterval);
          simulatedProgressInterval = null;
        }
      };
      
      // Initialize progress - start from 0%
      updateProgress();
      startSimulatedProgress();
      
      // Upload files sequentially (one at a time)
      for (let fileIndex = 0; fileIndex < pendingFootageFiles.length; fileIndex++) {
        const file = pendingFootageFiles[fileIndex];
        const fileChunkCount = fileChunkCounts[fileIndex].chunkCount;
        
        try {
          // Calculate chunks from previous files
          const previousFileChunks = fileChunkCounts
            .slice(0, fileIndex)
            .reduce((sum, item) => sum + item.chunkCount, 0);
          
          // Set initial target to first chunk completion of this file
          targetProgress = (previousFileChunks + 1) * progressPerChunk;
          
          // Upload file with progress tracking
          const task = await asyncUploadService.uploadFile(
            track.id, 
            file, 
            'footages',
            fileChunkCount === 1 ? (fileProgress) => {
              if (fileProgress >= 100) {
                targetProgress = (previousFileChunks + 1) * progressPerChunk;
                currentSimulatedProgress = targetProgress;
                updateProgress();
                cleanupSimulatedProgress();
              }
            } : undefined,
            fileChunkCount > 1 ? (chunkIndex, totalChunksForFile) => {
              if (chunkIndex === 0) return;
              
              const globalChunkIndex = previousFileChunks + chunkIndex;
              const chunkCompletionProgress = globalChunkIndex * progressPerChunk;
              
              currentSimulatedProgress = chunkCompletionProgress;
              targetProgress = chunkCompletionProgress;
              updateProgress();
              
              if (globalChunkIndex < totalChunks) {
                targetProgress = (globalChunkIndex + 1) * progressPerChunk;
                startSimulatedProgress();
              } else {
                cleanupSimulatedProgress();
                currentSimulatedProgress = 100;
                targetProgress = 100;
                updateProgress();
              }
            } : undefined
          );
          
          // File upload completed
          const fileCompletionProgress = (previousFileChunks + fileChunkCount) * progressPerChunk;
          currentSimulatedProgress = fileCompletionProgress;
          targetProgress = fileCompletionProgress;
          updateProgress();
          
          // If this is not the last file, continue simulation
          const isLastFile = fileIndex === pendingFootageFiles.length - 1;
          if (!isLastFile) {
            const nextFileFirstChunk = previousFileChunks + fileChunkCount + 1;
            if (nextFileFirstChunk <= totalChunks) {
              targetProgress = nextFileFirstChunk * progressPerChunk;
              startSimulatedProgress();
            }
          } else {
            cleanupSimulatedProgress();
          }
          
          // Mark file as completed
          setFootageUploadFileStatuses(prev => ({
            ...prev,
            [file.name]: 'completed'
          }));
          
          successfulUploads.push(file.name);
        } catch (uploadError) {
          cleanupSimulatedProgress();
          
          const errorMessage = uploadError instanceof Error ? uploadError.message : String(uploadError);
          console.error('❌ Footage upload failed:', {
            fileName: file.name,
            trackId: track.id,
            error: errorMessage,
            fullError: uploadError
          });
          
          logError('Failed to upload footage file', { 
            trackId: track.id, 
            fileName: file.name, 
            error: errorMessage
          });
          
          // Mark file as failed
          setFootageUploadFileStatuses(prev => ({
            ...prev,
            [file.name]: 'failed'
          }));
          
          failedUploads.push(file.name);
        }
      }
      
      // All files completed - clean up and set to 100%
      cleanupSimulatedProgress();
      currentSimulatedProgress = 100;
      targetProgress = 100;
      setFootageUploadProgress(prev => ({
        ...prev,
        [overallUploadId]: {
          filename: allFileNames,
          progress: 100
        }
      }));
      
      // Clean up overall upload ID after a short delay
      setTimeout(() => {
        setUploadingFootages(prev => {
          const newSet = new Set(prev);
          newSet.delete(overallUploadId);
          return newSet;
        });
        setFootageUploadFilenames(prev => {
          const newState = { ...prev };
          delete newState[overallUploadId];
          return newState;
        });
        setFootageUploadProgress(prev => {
          const newState = { ...prev };
          delete newState[overallUploadId];
          return newState;
        });
        setFootageUploadFileStatuses({});
      }, 500);
      
      // Clear pending files
      setPendingFootageFiles([]);
      
      if (failedUploads.length > 0) {
        toast.error(`${failedUploads.length} footage file(s) failed to upload: ${failedUploads.join(', ')}`);
      }
      
    } catch (error) {
      if (simulatedProgressInterval) {
        clearInterval(simulatedProgressInterval);
        simulatedProgressInterval = null;
      }
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('❌ Critical error in footage upload handler:', {
        error: errorMessage,
        fullError: error,
        trackId: track.id
      });
      
      logError('Failed to start footage uploads', { trackId: track.id, error: errorMessage });
      toast.error('Failed to start footage uploads');
    } finally {
      setIsUploadingFootages(false);
    }
  }, [track.id, pendingFootageFiles, user?.uid]);

  const showVideos = section === 'both' || section === 'videos';
  const showFootages = section === 'both' || section === 'footages';

  if (binaryAccess === false) {
    return (
      <BinaryUnavailablePlaceholder
        reason={binaryInactiveReason}
        requiredProvider={requiredStorageProvider}
        viewerRole={viewerRole}
      />
    );
  }

  return (
    <div className="space-y-6 w-full">
      {/* Actual Videos Section (Ready Videos) */}
      {showVideos && (
      <div>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text)' }}>{t('track.verticalVideos.videosLabel')}</h3>
        <p className="mb-4" style={{ color: 'var(--color-muted)' }}>{t('track.verticalVideos.videosDescription')}</p>
        
        <div className="relative mb-4">
          {videoFolderUrl && (
            <a
              href={videoFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-2 right-2 text-gray-400 hover:text-blue-600 z-10"
              title={t('track.openCloudFolder')}
              style={{ lineHeight: 0 }}
            >
              <FaLink size={24} />
            </a>
          )}
          {permissions?.canUploadPromo ? (
            <FileUpload
              label={videos.length ? t('track.addMoreVideos') : t('track.uploadVideos')}
              fileCategory="video"
              onFile={handleVideoFileUpload}
              multiple={true}
              showPreview={false}
              data-testid="video-files-file-input"
            />
          ) : (
            <div className="border-2 border-dashed rounded-lg p-6 text-center opacity-50" style={{ borderColor: 'var(--color-border)' }}>
              <div>
                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                  Upload not available - View only access
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>
                  You can view existing videos below
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Selected Video Files Preview - Hidden when uploading */}
        {pendingVideoFiles.length > 0 && permissions?.canUploadPromo && !isUploadingVideos && (
          <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                {t('fileUpload.footageFilesSelected', { count: pendingVideoFiles.length })}
              </h4>
              <button
                onClick={() => setPendingVideoFiles([])}
                className="text-sm hover:opacity-80 transition-opacity"
                style={{ color: 'var(--color-text)' }}
              >
                {t('fileUpload.clear')}
              </button>
            </div>
            <div className="space-y-2">
              {pendingVideoFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm" style={{ color: 'var(--color-text)' }}>
                  <span className="truncate">{file.name}</span>
                  <span className="opacity-70">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                </div>
              ))}
            </div>
            <button
              onClick={handleBulkVideoUpload}
              disabled={isUploadingVideos}
              className="mt-3 w-full py-2 px-4 rounded-lg font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed btn-primary"
              data-testid="video-files-upload-button"
            >
              {`Upload ${pendingVideoFiles.length} Video${pendingVideoFiles.length === 1 ? '' : 's'}`}
            </button>
          </div>
        )}

        {/* Show cassette loader when uploading */}
        {isUploadingVideos && (
          <div className="mb-4 flex items-center justify-center py-8">
            <CassetteLoader size={64} />
          </div>
        )}

        {/* Upload Progress Display */}
        <UploadProgressDisplay
          uploadingFiles={uploadingVideos}
          uploadProgress={videoUploadProgress}
          uploadFilenames={videoUploadFilenames}
          uploadFileStatuses={videoUploadFileStatuses}
        />

        {videos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mt-6">
            {videos.map((file, idx) => (
              <FileTile
                key={file.fileId || file.name + '-' + idx}
                file={file}
                icon={getVideoIcon(file.name)}
                onRemove={permissions?.canUploadPromo ? () => {
                  if (window.confirm(t('track.confirmRemoveVideoFile'))) {
                    setVideos(prev => prev.filter((_, i) => i !== idx));
                    if (typeof deleteVideoFile === 'function') {
                      deleteVideoFile(track.id, file.fileId);
                    }
                  }
                } : undefined}
                removable={permissions?.canUploadPromo || false}
                showSocialIcons={true}
                variant="video"
                permissions={permissions}
                onSocialClick={permissions?.canUploadPromo ? async (type) => {
                  const key = type as SocialLinkKey;
                  const socialLinks = file.socialLinks || {};
                  const link = socialLinks[key];
                  
                  // Check if this is a remove action
                  if (type.startsWith('remove_')) {
                    const actualKey = type.replace('remove_', '') as SocialLinkKey;
                    if (track.id && file.fileId) {
                      const updatedSocialLinks = { ...socialLinks };
                      delete updatedSocialLinks[actualKey];
                      await updateVideoSocialLinks(track.id, file.fileId, updatedSocialLinks);
                      setVideos(prev => prev.map(f => f.fileId === file.fileId ? { ...f, socialLinks: updatedSocialLinks } : f));
                    }
                    return;
                  }
                  
                  // Handle add/edit (both use the same prompt)
                  const currentValue = link || '';
                  const action = link ? 'Edit' : 'Add';
                  const newLink = prompt(`${action} ${key.replace(/([A-Z])/g, ' $1')} link:`, currentValue);
                  
                  if (newLink !== null && track.id && file.fileId) { // null means user cancelled
                    try {
                      if (newLink.trim() === '') {
                        // Empty string means remove the link
                        const updatedSocialLinks = { ...socialLinks };
                        delete updatedSocialLinks[key];
                        await updateVideoSocialLinks(track.id, file.fileId, updatedSocialLinks);
                        setVideos(prev => prev.map(f => f.fileId === file.fileId ? { ...f, socialLinks: updatedSocialLinks } : f));
                      } else {
                        // Update/add the link
                        const updatedSocialLinks = { ...socialLinks, [key]: newLink.trim() };
                        await updateVideoSocialLinks(track.id, file.fileId, updatedSocialLinks);
                        setVideos(prev => prev.map(f => f.fileId === file.fileId ? { ...f, socialLinks: updatedSocialLinks } : f));
                      }
                    } catch (error) {
                      // Silent error; toast below will inform user
                      toast.error('Failed to update social link. Please try again.');
                    }
                  }
                } : undefined}
              />
            ))}
          </div>
        )}
      </div>
      )}

      {/* Footages Section (Raw Footages) */}
      {showFootages && (
      <div>
        <h3 className="text-lg font-medium mb-4" style={{ color: 'var(--color-text)' }}>{t('track.verticalVideos.footagesLabel')}</h3>
        <p className="mb-4" style={{ color: 'var(--color-muted)' }}>{t('track.verticalVideos.footagesDescription')}</p>
        
        <div className="relative mb-4">
          {footagesFolderUrl && (
            <a
              href={footagesFolderUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-2 right-2 text-gray-400 hover:text-blue-600 z-10"
              title={t('track.openCloudFolder')}
              style={{ lineHeight: 0 }}
            >
              <FaLink size={24} />
            </a>
          )}
          {permissions?.canUploadPromo ? (
            <FileUpload
              label={footages.length ? t('track.addMoreFootages') : t('track.uploadFootages')}
              fileCategory="video"
              onFile={handleFootageFileUpload}
              multiple={true}
              showPreview={false}
              data-testid="footage-files-file-input"
            />
          ) : (
            <div className="border-2 border-dashed rounded-lg p-6 text-center opacity-50" style={{ borderColor: 'var(--color-border)' }}>
              <div>
                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                  Upload not available - View only access
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-muted)' }}>
                  You can view existing footages below
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Selected Footage Files Preview - Hidden when uploading */}
        {pendingFootageFiles.length > 0 && permissions?.canUploadPromo && !isUploadingFootages && (
          <div className="mb-4 p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-hover)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                {t('fileUpload.footageFilesSelected', { count: pendingFootageFiles.length })}
              </h4>
              <button
                onClick={() => setPendingFootageFiles([])}
                className="text-sm hover:opacity-80 transition-opacity"
                style={{ color: 'var(--color-text)' }}
              >
                {t('fileUpload.clear')}
              </button>
            </div>
            <div className="space-y-2">
              {pendingFootageFiles.map((file, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm" style={{ color: 'var(--color-text)' }}>
                  <span className="truncate">{file.name}</span>
                  <span className="opacity-70">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                </div>
              ))}
            </div>
            <button
              onClick={handleBulkFootageUpload}
              disabled={isUploadingFootages}
              className="mt-3 w-full py-2 px-4 rounded-lg font-medium transition-opacity disabled:opacity-50 disabled:cursor-not-allowed btn-primary"
              data-testid="footage-files-upload-button"
            >
              {t('promo.uploadFootageFile', { count: pendingFootageFiles.length })}
            </button>
          </div>
        )}

        {/* Show cassette loader when uploading */}
        {isUploadingFootages && (
          <div className="mb-4 flex items-center justify-center py-8">
            <CassetteLoader size={64} />
          </div>
        )}

        {/* Upload Progress Display */}
        <UploadProgressDisplay
          uploadingFiles={uploadingFootages}
          uploadProgress={footageUploadProgress}
          uploadFilenames={footageUploadFilenames}
          uploadFileStatuses={footageUploadFileStatuses}
        />

        {footages.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 mt-6">
            {footages.map((file, idx) => (
              <FileTile
                key={file.fileId || file.name + '-' + idx}
                file={file}
                icon={getVideoIcon(file.name)}
                onRemove={permissions?.canUploadPromo ? () => {
                  if (window.confirm(t('track.confirmRemoveFootageFile'))) {
                    setFootages(prev => prev.filter((_, i) => i !== idx));
                    if (typeof deleteFootageFile === 'function') {
                      deleteFootageFile(track.id, file.fileId);
                    }
                  }
                } : undefined}
                removable={permissions?.canUploadPromo || false}
                showSocialIcons={false}
                variant="footage"
                permissions={permissions}
              />
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default VerticalVideosSection; 